import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import OpenInNewIcon from '@material-ui/icons/OpenInNew';
export const _frontmatter = {
  "title": "Federal sales",
  "includeToc": true,
  "layout": "downloads",
  "description": "This dataset contains federal sales data for oil, gas, and natural gas liquids (NGLs) by calendar year. The dataset is for years 2013-2023.",
  "tags": ["Data", "Downloads", "Documentation", "Federal", "Effective royalty rates", "Allowances", "Royalties", "Revenue", "Location", "By location", "Calendar year", "Sales"]
};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const GlossaryTerm = makeShortcode("GlossaryTerm");
const DownloadDataFile = makeShortcode("DownloadDataFile");
const Link = makeShortcode("Link");
const SalesDataDiagram = makeShortcode("SalesDataDiagram");
const FederalSalesTransactionCodesTable = makeShortcode("FederalSalesTransactionCodesTable");
const FederalSalesProductCodesTable = makeShortcode("FederalSalesProductCodesTable");
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p><a parentName="p" {...{
        "href": "/sites/whats-new-v7.10.0/downloads/"
      }}>{`Downloads /`}</a></p>

    <h1 {...{
      "id": "Federal-sales",
      "style": {
        "position": "relative"
      }
    }}>{`Federal sales`}</h1>
    <p>{`This dataset contains federal sales data from the Form ONRR-2014 for:`}</p>
    <ul>
      <li parentName="ul">{`oil`}</li>
      <li parentName="ul">{`gas`}</li>
      <li parentName="ul"><GlossaryTerm mdxType="GlossaryTerm">natural gas liquids (NGLs)</GlossaryTerm>  </li>
    </ul>
    <p>{`This dataset contains reported volumes for royalty and non-royalty bearing transactions by calendar year. Federal sales data represents sales transactions that took place within a given calendar year. This dataset is for calendar years 2013-2023.  `}</p>
 <br />  
    <p>{`Download federal sales data (as of March 6, 2024):`}</p>
    <DownloadDataFile linkType="DownloadCsv" dataSet="federal_sales.csv" mdxType="DownloadDataFile">
  Calendar year federal sales data
    </DownloadDataFile>
    <DownloadDataFile linkType="DownloadXls" dataSet="federal_sales.xlsx" mdxType="DownloadDataFile">
  Calendar year federal sales data, data dictionary and notes
    </DownloadDataFile>  
    <Link href='/query-data/?dataType=Federal%20Sales' linkType='FilterTable' mdxType="Link">Filter and query data to customize the dataset</Link>
    <h2 {...{
      "id": "Scope",
      "style": {
        "position": "relative"
      }
    }}>{`Scope`}</h2>
    <p>{`ONRR collects the data from the Sales and Royalty Remittance Report, Form ONRR-2014. Reporters submit Form ONRR-2014 to provide information on royalties due based on sales or use of the natural resource production.`}</p>
    <br />
ONRR collects revenues on over 60 different products, but this dataset only includes data for:
    <ul>
      <li parentName="ul">{`oil, gas, and NGLs on U.S. federal onshore lands`}</li>
      <li parentName="ul">{`oil, gas, and NGLs on U.S. federal offshore areas`}</li>
    </ul>
    <p>{`It does not include Native American land, privately-owned lands, or U.S. state lands. The dataset includes all ONRR Form-2014 federal reported royalty transaction codes, royalty relief codes, and transaction code (TC) 13 Quality Bank and Gravity Bank Adjustments.`}</p>
    <br />
After reporters submit Form ONRR-2014, ONRR aggregates the data by totaling the data values by the sales year. ONRR then publishes this dataset.
    <br />
    <h2 {...{
      "id": "Data-publication",
      "style": {
        "position": "relative"
      }
    }}>{`Data publication`}</h2>
    <p>{`The federal sales dataset is updated annually. `}</p>
    <h2 {...{
      "id": "About-the-data",
      "style": {
        "position": "relative"
      }
    }}>{`About the data`}</h2>
    <h3 {...{
      "id": "What-are-the-steps-to-develop-federal-sales-data",
      "style": {
        "position": "relative"
      }
    }}>{`What are the steps to develop federal sales data?`}</h3>
    <p>{`Review the step-by-step diagram to learn about how the federal sales data is developed. Use the arrows to expand the steps and learn more about the process.`}</p>
    <SalesDataDiagram mdxType="SalesDataDiagram" />
    <h3 {...{
      "id": "What-is-the-difference-between-sales-year-and-accounting-year-data",
      "style": {
        "position": "relative"
      }
    }}>{`What is the difference between sales year and accounting year data?`}</h3>
    <p>{`The federal sales dataset is sales year data reported on a calendar year basis. Although sales year and accounting year appear similar, there are key differences:  `}</p>
    <br />
    <b>Sales year</b> data includes sales transactions records for a specific year* without adjustments for previous years. This data provides a snapshot of reported sales. It is useful for analyzing sales trends and comparing them to production volumes. The data changes, so ONRR publishes them with an as-of date. The federal sales dataset is sales year data reported on a calendar year basis.
    <br /> 
    <br /> 
    <b>Accounting year</b> data includes sales transaction records accepted by ONRR within a specific year* with adjustments for previous years. The data shows the money ONRR collected and disbursed from the current year and any adjustments or corrections for previous years. It does not provide accurate trend analyses of sales values or volumes over time.
    <br />
    <br />
*federal fiscal year or calendar year
    <br />
    <h3 {...{
      "id": "What-are-transaction-codes-TC",
      "style": {
        "position": "relative"
      }
    }}>{`What are transaction codes (TC)?`}</h3>
    <p>{`Transaction codes are used transactions on Form ONRR-2014 and organized by function. This dataset does not include all the transaction codes. Other datasets on the site that come from Form ONRR-2014 include all the transaction codes. There are several reasons why some transaction codes are excluded for this dataset. The reasons include not specific to royalty collection, being used to only provide estimates, not relating to oil and gas products, not having royalties reported, or relating to minimum payments. `}</p>
    <h4 {...{
      "id": "Federal-sales-dataset-transaction-codes",
      "style": {
        "position": "relative"
      }
    }}>{`Federal sales dataset transaction codes`}</h4>
    <FederalSalesTransactionCodesTable mdxType="FederalSalesTransactionCodesTable" />
    <h3 {...{
      "id": "What-are-product-codes",
      "style": {
        "position": "relative"
      }
    }}>{`What are product codes?`}</h3>
    <p>{`Product codes define the product or commodity on Form ONRR-2014. This dataset does not include all product codes reported on all Form ONRR-2014s. This dataset only includes product codes related to the product and byproducts of oil, gas, and NGLs.`}</p>
    <h4 {...{
      "id": "Federal-sales-dataset-product-codes",
      "style": {
        "position": "relative"
      }
    }}>{`Federal sales dataset product codes`}</h4>
    <FederalSalesProductCodesTable mdxType="FederalSalesProductCodesTable" />
    <h3 {...{
      "id": "How-are-quality-and-gravity-bank-adjustments-calculated",
      "style": {
        "position": "relative"
      }
    }}>{`How are quality and gravity bank adjustments calculated?`}</h3>
    <p>{`Quality and gravity bank adjustments are made prior to any allowances taking place and are included in the reporting. These adjustments (TC 13) are used to report the difference in quality (gravity) between oil measured at the BLM or BSEE-approved point of royalty settlement and the oil delivered to the refiner at the delivery point. The dataset rolls up the TC 13 adjustments with the TC 01 (Royalty) reporting for oil. (See `}<a parentName="p" {...{
        "href": "https://www.ecfr.gov/current/title-30/chapter-XII/subchapter-A/part-1206"
      }}>{`30 CFR 1206.119 `}<OpenInNewIcon style={{
          fontSize: 20
        }} mdxType="OpenInNewIcon" /></a>{`.)`}</p>
    <h3 {...{
      "id": "What-does-not-tied-to-a-commodity-mean",
      "style": {
        "position": "relative"
      }
    }}>{`What does “not tied to a commodity” mean?`}</h3>
    <p>{`As a part of the COVID royalty relief program reporters were not required to tie their reporting to a specific product code. Due to this ONRR is unable to allocate the reported information to the various products. In this dataset, “not tied to a commodity” references transactions that are identified as COVID royalty relief.`}</p>
    <h3 {...{
      "id": "What-is-COVID-royalty-relief",
      "style": {
        "position": "relative"
      }
    }}>{`What is COVID royalty relief?`}</h3>
    <p>{`Under `}<a parentName="p" {...{
        "href": "https://www.ecfr.gov/current/title-30/chapter-II/subchapter-A/part-203"
      }}>{`30 CFR 203.80 `}<OpenInNewIcon style={{
          fontSize: 20
        }} mdxType="OpenInNewIcon" /></a>{` the Bureau of Safety and Environmental Enforcement (BSEE) can grant special case royalty relief on a product or lease basis. This royalty relief is granted through an application-based process that adheres to strict criteria. In 2020 and 2021, BSEE granted limited royalty relief known as COVID royalty relief. COVID royalty relief is separated in this dataset because payors were not required to report a product.`}</p>
    <h2 {...{
      "id": "Data-dictionary",
      "style": {
        "position": "relative"
      }
    }}>{`Data dictionary`}</h2>
    <h3 {...{
      "id": "Fields-and-definitions",
      "style": {
        "position": "relative"
      }
    }}>{`Fields and definitions`}</h3>
    <p><strong parentName="p">{`Calendar Year:`}</strong>{` The period between January 1 and December 31 for a given year. This dataset is sales year data reported on a calendar year basis. `}</p>
    <br />
    <p><strong parentName="p">{`Land Class:`}</strong>{` This field distinguishes federal lands and waters from Native American lands. All data in this dataset is federal.`}</p>
    <ul>
      <li parentName="ul"><strong parentName="li">{`Federal:`}</strong>{`  `}<a parentName="li" {...{
          "href": "https://sgp.fas.org/crs/misc/R42346.pdf"
        }}>{`Federal lands (PDF)`}<OpenInNewIcon style={{
            fontSize: 20
          }} mdxType="OpenInNewIcon" /></a>{` are owned by or under the jurisdiction of the federal government. Federal lands include: public domain lands, acquired lands, military acquired lands, and the `}<a parentName="li" {...{
          "href": "https://www.boem.gov/oil-gas-energy/leasing/outer-continental-shelf"
        }}>{`Outer Continental Shelf `}<OpenInNewIcon style={{
            fontSize: 20
          }} mdxType="OpenInNewIcon" /></a>{`.`}</li>
    </ul>
    <p><strong parentName="p">{`Land Category:`}</strong></p>
    <ul>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p">{`Onshore:`}</strong>{` Situated or occurring on land.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p">{`Offshore:`}</strong>{` Submerged lands located farther than three miles off a state’s coastline, or three marine leagues into the Gulf of Mexico off Texas and western Florida.`}</p>
      </li>
    </ul>
    <p><strong parentName="p">{`State/Offshore Region:`}</strong>{` The Bureau of Ocean Energy Management (BOEM) separates offshore areas into four regions: Gulf of Mexico, Atlantic, Pacific, and Alaska. For more information on offshore regions, including spatial boundaries, see the Bureau of Ocean Energy Management's `}<a parentName="p" {...{
        "href": "https://www.boem.gov/oil-gas-energy/mapping-and-data"
      }}>{`maps and GIS data `}<OpenInNewIcon style={{
          fontSize: 20
        }} mdxType="OpenInNewIcon" /></a>{`. The state name is also listed under this column.`}</p>
    <br />
    <p><strong parentName="p">{`Revenue Type:`}</strong>{` Revenues from U.S. natural resources fall into one of several types. This dataset contains only royalties and COVID royalty relief as revenue types.`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p">{`Royalties:`}</strong>{` A natural resource lease holder pays royalties after the lease starts producing a commodity in paying quantities. The amount is based on a percentage of the revenue from the commodity sold. The exact percentage is set in the original lease document that went along with the lease sale.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p">{`Covid Royalty Relief:`}</strong>{` This amount is always negative. COVID royalty relief is given its own category in the data as payors were not required to report a product.`}</p>
      </li>
    </ul>
    <p><strong parentName="p">{`Commodity:`}</strong>{` The Department of the Interior collects revenues on over 60 different products, but there are only oil, gas, NGLs, and "not tied to a commodity" in this dataset. “Not tied to a commodity” references COVID royalty relief data. `}</p>
    <br />
    <p><strong parentName="p">{`Sales Volume:`}</strong>{` A reported field on the Form ONRR-2014. Sales Volume is the quantity and quality of production as measured at the approved royalty measurement point. It is then sold. The Bureau of Land Management (BLM) or Bureau of Safety and Environmental Enforcement (BSEE) approve measuring points.`}</p>
    <br />
    <p><strong parentName="p">{`Gas MMBtu Volume:`}</strong>{` Volume measured in Million British Thermal Units (MMBtu). One British Thermal Unit (Btu) is the amount of heat required to raise the temperature of one pound of water by one degree Fahrenheit. MMBtu is the globally used energy value measurement. `}</p>
    <br />
    <p><strong parentName="p">{`Unit Value:`}</strong>{` The per-unit price that you apply to the royalty-bearing volume.`}</p>
    <br />
    <p><strong parentName="p">{`Sales Value:`}</strong>{` A reported field on the Form ONRR-2014. Reporters calculate sales value by multiplying the volumes sold by the agreed upon unit price. This calculation is done for each product based on pricing contracts and approved valuation guidelines.`}</p>
    <br />
    <p><strong parentName="p">{`Royalty Value Prior to Allowances (RVPA):`}</strong>{` Calculated by multiplying sales value by royalty rate.`}</p>
    <br />
    <p><strong parentName="p">{`Transportation Allowances (TA):`}</strong>{` Deductions in determining royalty value for the reasonable, actual costs incurred by the lessee, or a deduction accepted or established by ONRR for moving coal from the mine or lease to a remote point of sale or delivery, or moving oil or gas, gas, or geothermal byproducts to a point of sale or delivery off of the lease, unit area, or communitized area.`}</p>
    <br />
    <p><strong parentName="p">{`Processing Allowances (PA):`}</strong>{` Deductions from royalty value for the lessee's actual, reasonable costs of processing a gas stream.`}</p>
    <br />
    <p><strong parentName="p">{`Royalty Value Less Allowances (RVLA):`}</strong>{` Calculated by subtracting allowances (transportation, processing) from royalty value.`}</p>
    <br />
    <p><strong parentName="p">{`Effective Royalty Rate:`}</strong>{` The Office of Inspector General (OIG) created the term and concept of effective royalty rate. The OIG defines the calculation of the effective royalty rate in `}<a parentName="p" {...{
        "href": "https://www.doioig.gov/reports/inspection-evaluation/us-department-interior-does-not-analyze-effective-royalty-rates"
      }}>{`Report 2021-CR-042 June 2023 `}<OpenInNewIcon style={{
          fontSize: 20
        }} mdxType="OpenInNewIcon" /></a>{`.`}</p>
    <br />

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      